/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { DbResult, Guid, Id64String } from "@itwin/core-bentley";
import { ChangesetIdWithIndex, ChangesetProps, Code, GeometricElement2dProps, GeometricModelProps, GeometryStreamBuilder, IModel, ModelGeometryChangesProps, ModelIdAndGeometryGuid, QueryBinder, RelatedElementProps, RelationshipProps, SubCategoryAppearance, TxnProps } from "@itwin/core-common";
import * as chai from "chai";
import * as chaiAsPromised from "chai-as-promised";
import { Suite } from "mocha";
import { HubWrappers, IModelTestUtils, KnownTestLocations } from "..";
import { _nativeDb, BriefcaseDb, BriefcaseManager, ChangesetECAdaptor, ChannelControl, DrawingCategory, ElementGroupsMembers, SqliteChangesetReader, TxnIdString } from "../../core-backend";
import { HubMock } from "../../internal/HubMock";
import { StashManager } from "../../StashManager";
import { EditTxn } from "../../EditTxn";
import { TestUtils } from "../TestUtils";
import { existsSync, unlinkSync, writeFileSync } from "fs";
import * as path from "path";
import { LineSegment3d, Point3d } from "@itwin/core-geometry";
chai.use(chaiAsPromised);

function startTestTxn(iModel: BriefcaseDb, description = "rebase"): EditTxn {
  const txn = new EditTxn(iModel, description);
  txn.start();
  return txn;
}

async function importSchemaStrings(txn: EditTxn, schemas: string[]): Promise<void> {
  if (txn.isActive)
    txn.saveChanges();
  await txn.iModel.importSchemaStrings(schemas);
}


class TestIModel {
  public iModelId: Id64String = "";
  public drawingModelId: Id64String = "";
  public drawingCategoryId: Id64String = "";
  public briefcases: BriefcaseDb[] = [];
  private _data = 0;
  private _nextAccessToken = 1;
  public constructor() { }

  private acquireAccessToken(): string {
    return `user${this._nextAccessToken++}`;
  }

  public async startup() {
    HubMock.startup("TestIModel", KnownTestLocations.outputDir);
    const accessToken = this.acquireAccessToken();
    this.iModelId = await HubMock.createNewIModel({ accessToken, iTwinId: HubMock.iTwinId, iModelName: "Test", description: "TestSubject" });
    const b1 = await HubWrappers.downloadAndOpenBriefcase({ accessToken, iTwinId: HubMock.iTwinId, iModelId: this.iModelId });
    const b1Txn = startTestTxn(b1, "rebase startup");
    b1.channels.addAllowedChannel(ChannelControl.sharedChannelName);
    const schema1 = `<?xml version="1.0" encoding="UTF-8"?>
    <ECSchema schemaName="TestDomain" alias="ts" version="01.00.00" xmlns="http://www.bentley.com/schemas/Bentley.ECXML.3.2">
        <ECSchemaReference name="BisCore" version="01.00.00" alias="bis"/>
        <ECEntityClass typeName="a1">
            <BaseClass>bis:GraphicalElement2d</BaseClass>
            <ECProperty propertyName="prop1" typeName="string" />
        </ECEntityClass>
        <ECEntityClass typeName="A1Recipe2d">
            <BaseClass>bis:TemplateRecipe2d</BaseClass>
            <ECProperty propertyName="prop1" typeName="string" />
        </ECEntityClass>
        <ECRelationshipClass typeName="A1OwnsA1" modifier="None" strength="embedding">
            <BaseClass>bis:ElementOwnsChildElements</BaseClass>
            <Source multiplicity="(0..1)" roleLabel="owns" polymorphic="true">
                <Class class="a1"/>
            </Source>
            <Target multiplicity="(0..*)" roleLabel="is owned by" polymorphic="false">
                <Class class="a1"/>
            </Target>
        </ECRelationshipClass>
    </ECSchema>`;

    await importSchemaStrings(b1Txn, [schema1]);
    chai.expect(b1.txns.hasPendingTxns).to.be.true
    await b1.pushChanges({ description: "schema1" });
    const codeProps = Code.createEmpty();
    codeProps.value = "DrawingModel";
    await b1.locks.acquireLocks({ shared: IModel.dictionaryId });
    this.drawingModelId = IModelTestUtils.createAndInsertDrawingPartitionAndModel(b1Txn, codeProps, true)[1];
    let drawingCategoryId = DrawingCategory.queryCategoryIdByName(b1, IModel.dictionaryId, "MyDrawingCategory");
    if (undefined === drawingCategoryId)
      drawingCategoryId = DrawingCategory.insert(b1Txn, IModel.dictionaryId, "MyDrawingCategory", new SubCategoryAppearance());
    this.drawingCategoryId = drawingCategoryId;

    b1Txn.saveChanges();
    await b1.pushChanges({ description: "drawing category" });
    b1.close();
  }
  public async openBriefcase(): Promise<BriefcaseDb> {
    const b = await HubWrappers.downloadAndOpenBriefcase({ accessToken: this.acquireAccessToken(), iTwinId: HubMock.iTwinId, iModelId: this.iModelId });
    b.channels.addAllowedChannel(ChannelControl.sharedChannelName);
    b[_nativeDb].saveChanges();
    this.briefcases.push(b);
    return b;
  }
  public async insertRecipe2d(txn: EditTxn, markAsIndirect?: true) {
    const b = txn.iModel as BriefcaseDb;
    await b.locks.acquireLocks({ shared: [IModel.dictionaryId] });
    const baseProps = {
      classFullName: "TestDomain:A1Recipe2d",
      model: IModel.dictionaryId,
      code: Code.createEmpty(),
    };

    let id: Id64String = "";
    if (markAsIndirect) {
      b.txns.withIndirectTxnMode(() => {
        id = txn.insertElement({ ...baseProps, prop1: `${this._data++}` } as any);
      });
      return id;
    }
    return txn.insertElement({ ...baseProps, prop1: `${this._data++}` } as any);
  }
  public async updateRecipe2d(txn: EditTxn, id: Id64String, markAsIndirect?: true) {
    const b = txn.iModel as BriefcaseDb;
    await b.locks.acquireLocks({ shared: [IModel.dictionaryId], exclusive: [id] });
    const elProps = b.elements.getElementProps(id);

    if (markAsIndirect) {
      b.txns.withIndirectTxnMode(() => {
        txn.updateElement({ ...elProps, prop1: `${this._data++}` });
      });
    } else {
      txn.updateElement({ ...elProps, prop1: `${this._data++}` });
    }
  }
  public async insertElement(txn: EditTxn, markAsIndirect?: true) {
    const b = txn.iModel as BriefcaseDb;
    await b.locks.acquireLocks({ shared: [this.drawingModelId] });
    const builder = new GeometryStreamBuilder();
    const p1 = Point3d.createZero();
    const p2 = Point3d.createFrom({ x: Math.random() * 10.0 + 5.0, y: 0.0, z: 0.0 });
    const circle = LineSegment3d.create(p1, p2);
    builder.appendGeometry(circle);

    const baseProps = {
      classFullName: "TestDomain:a1",
      model: this.drawingModelId,
      category: this.drawingCategoryId,
      code: Code.createEmpty(),
      geom: builder.geometryStream,
      prop1: `${this._data++}`,
    } as GeometricElement2dProps & { prop1: string };

    let id: Id64String = "";
    if (markAsIndirect) {
      b.txns.withIndirectTxnMode(() => {
        id = txn.insertElement(baseProps);
      });
      return id;
    }
    baseProps.prop1 = `${this._data++}`;
    return txn.insertElement(baseProps);
  }
  public async insertElementEx(txn: EditTxn, args?: { prop1?: string, markAsIndirect?: true, parent?: RelatedElementProps }) {
    const b = txn.iModel as BriefcaseDb;
    await b.locks.acquireLocks({ shared: [this.drawingModelId] });
    const builder = new GeometryStreamBuilder();
    const p1 = Point3d.createZero();
    const p2 = Point3d.createFrom({ x: Math.random() * 10.0 + 5.0, y: 0.0, z: 0.0 });
    const circle = LineSegment3d.create(p1, p2);
    builder.appendGeometry(circle);

    const props: GeometricElement2dProps & { prop1: string } = {
      classFullName: "TestDomain:a1",
      model: this.drawingModelId,
      category: this.drawingCategoryId,
      code: Code.createEmpty(),
      parent: args?.parent,
      geom: builder.geometryStream,
      prop1: args?.prop1 ?? `${this._data++}`
    };

    let id: Id64String = "";
    if (args?.markAsIndirect) {
      b.txns.withIndirectTxnMode(() => {
        id = txn.insertElement(props);
      });
      return id;
    }
    return txn.insertElement(props);
  }
  public async updateElement(txn: EditTxn, id: Id64String, markAsIndirect?: true, updateGeom?: boolean) {
    const b = txn.iModel as BriefcaseDb;
    await b.locks.acquireLocks({ shared: [this.drawingModelId], exclusive: [id] });
    const elProps = b.elements.getElementProps<GeometricElement2dProps & { prop1: string }>(id);

    if (updateGeom) {
      const builder = new GeometryStreamBuilder();
      const p1 = Point3d.createZero();
      const p2 = Point3d.createFrom({ x: Math.random() * 10.0 + 10.0, y: 0.0, z: 0.0 });
      const circle = LineSegment3d.create(p1, p2);
      builder.appendGeometry(circle);
      elProps.geom = builder.geometryStream;
    }
    if (markAsIndirect) {
      b.txns.withIndirectTxnMode(() => {
        txn.updateElement({ ...elProps, prop1: `${this._data++}` });
      });
    } else {
      txn.updateElement({ ...elProps, prop1: `${this._data++}` });
    }
  }
  public async deleteElement(txn: EditTxn, id: Id64String, markAsIndirect?: true) {
    const b = txn.iModel as BriefcaseDb;
    await b.locks.acquireLocks({ shared: [this.drawingModelId], exclusive: [id] });
    if (markAsIndirect) {
      b.txns.withIndirectTxnMode(() => {
        txn.deleteElement(id);
      });
    } else {
      txn.deleteElement(id);
    }
  }
  public async shutdown(): Promise<void> {
    this.briefcases.forEach(b => b.close());
    HubMock.shutdown();
  }
}

const removePropertyRecursive = (obj: any, prop: string) => {
  if (obj && typeof obj === "object") {
    Object.keys(obj).forEach((key) => {
      if (key === prop) {
        delete obj[key];
      } else {
        removePropertyRecursive(obj[key], prop);
      }
    });
  }
};

for (const enableSemanticRebase of [false, true]) {
  describe(`rebase changes & stashing api (useSemanticRebase=${enableSemanticRebase})`, function (this: Suite) {
    let testIModel: TestIModel;
    before(async () => {
      await TestUtils.shutdownBackend();
      await TestUtils.startBackend({ useSemanticRebase: enableSemanticRebase });
    });
    after(async () => {
      await TestUtils.shutdownBackend();
      await TestUtils.startBackend(); // restart normal backend so subsequent test suites aren't left without IModelHost
    });
    this.beforeEach(async () => {
      testIModel = new TestIModel();
      await testIModel.startup();
    });
    this.afterEach(async () => {
      await testIModel.shutdown();
    });
    it("save changes args", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "save changes args");
      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges({
        source: "test",
        description: "test description",
        appData: {
          test: "test",
          foo: [1, 2, 3],
          bar: { baz: "qux" }
        }
      });

      let lastTxn = b1.txns.getLastSavedTxnProps();
      chai.assert.isDefined(lastTxn);
      if (lastTxn) {
        chai.expect(lastTxn.props.source).to.be.equals("test");
        chai.expect(lastTxn.props.description).to.be.equals("test description");
        chai.expect(lastTxn.props.appData).to.not.be.undefined;
        chai.expect(lastTxn.props.appData?.test).to.be.eq("test");
        chai.expect(lastTxn.props.appData?.foo).to.be.deep.eq([1, 2, 3]);
        chai.expect(lastTxn.props.appData?.bar).to.be.deep.eq({ baz: "qux" });
        chai.expect(lastTxn.nextId).to.be.undefined;
        chai.expect(lastTxn.prevId).to.be.undefined;
        chai.expect(lastTxn.type).to.be.eq("Data");
        chai.expect(lastTxn.id).to.be.eq('0x100000000');
        chai.expect(lastTxn.reversed).to.be.false;
        chai.expect(lastTxn.grouped).to.be.false;
      }

      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges({
        source: "test2",
        description: "test description 2",
        appData: {
          test: "test 2",
          foo: [11, 12, 13],
          bar: { baz: "qux2" }
        }
      });

      lastTxn = b1.txns.getLastSavedTxnProps();
      chai.assert.isDefined(lastTxn);
      if (lastTxn) {
        chai.expect(lastTxn.props.source).to.be.equals("test2");
        chai.expect(lastTxn.props.description).to.be.equals("test description 2");
        chai.expect(lastTxn.props.appData).to.not.be.undefined;
        chai.expect(lastTxn.props.appData?.test).to.be.eq("test 2");
        chai.expect(lastTxn.props.appData?.foo).to.be.deep.eq([11, 12, 13]);
        chai.expect(lastTxn.props.appData?.bar).to.be.deep.eq({ baz: "qux2" });
        chai.expect(lastTxn.nextId).to.be.undefined;
        chai.expect(lastTxn.prevId).to.be.equal('0x100000000');
        chai.expect(lastTxn.type).to.be.eq("Data");
        chai.expect(lastTxn.id).to.be.eq('0x100000001');
        chai.expect(lastTxn.reversed).to.be.false;
        chai.expect(lastTxn.grouped).to.be.false;
      }

      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges("new element");
      lastTxn = b1.txns.getLastSavedTxnProps();
      chai.assert.isDefined(lastTxn);
      if (lastTxn) {
        chai.expect(lastTxn.props.source).is.undefined;
        chai.expect(lastTxn.props.description).to.be.equals("new element");
        chai.expect(lastTxn.props.appData).to.be.undefined;
        chai.expect(lastTxn.nextId).to.be.undefined;
        chai.expect(lastTxn.prevId).to.be.equal('0x100000001');
        chai.expect(lastTxn.type).to.be.eq("Data");
        chai.expect(lastTxn.id).to.be.eq('0x100000002');
        chai.expect(lastTxn.reversed).to.be.false;
        chai.expect(lastTxn.grouped).to.be.false;
      }

      await b1.pushChanges({ description: "new element" });
      chai.expect(b1.txns.isUndoPossible).is.false;
      chai.expect(b1.txns.isRedoPossible).is.false;
      lastTxn = b1.txns.getLastSavedTxnProps();
      chai.assert.isUndefined(lastTxn);
    });

    it("direct / indirect", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "direct indirect");
      const directElId = await testIModel.insertElement(b1Txn);
      const indirectElId = await testIModel.insertElement(b1Txn, true);
      chai.expect(directElId).to.not.be.undefined;
      chai.expect(indirectElId).to.not.be.undefined;
      b1Txn.saveChanges({ description: "insert element 1 direct and 1 indirect" });
      const txn = b1.txns.getLastSavedTxnProps();
      chai.assert.isDefined(txn);
      if (txn) {
        let checkCount = 0;
        const reader = SqliteChangesetReader.openTxn({ txnId: txn?.id, db: b1 });
        while (reader.step()) {
          if (reader.primaryKeyValues.length === 0) continue;
          if (reader.tableName !== "bis_Element") continue;
          const iid = reader.primaryKeyValues[0] as Id64String;
          if (iid === directElId) {
            chai.expect(reader.isIndirect).to.be.false;
          }
          if (iid === indirectElId) {
            chai.expect(reader.isIndirect).to.be.true;
          }
          checkCount++;
        }
        chai.expect(checkCount).to.be.equals(2);
      }

      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });
      chai.expect(b1.txns.isUndoPossible).is.false;
      chai.expect(b1.txns.isRedoPossible).is.false;
      const lastTxn = b1.txns.getLastSavedTxnProps();
      chai.assert.isUndefined(lastTxn);
    });

    it("rebase handler", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "rebase handler b1");
      const b2Txn = startTestTxn(b2, "rebase handler b2");

      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });

      await b2.pullChanges();

      await testIModel.updateElement(b1Txn, e1);
      await testIModel.updateElement(b1Txn, e2, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "update element 1 direct and 1 indirect" });


      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("first change");

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("second change");

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("third change");

      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (_txn: TxnProps): Promise<void> => {
          await testIModel.insertElement(b2Txn);
          await testIModel.insertElement(b2Txn, true);
        },
      });
      await b1.pullChanges();
    });
    it("stash & drop", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "stash drop");
      const e1 = await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });

      const e2 = await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });

      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges(`first`);
      await testIModel.updateElement(b1Txn, e1);
      b1Txn.saveChanges(`second`);
      await testIModel.deleteElement(b1Txn, e2);
      b1Txn.saveChanges(`third`);
      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges(`fourth`);

      const stash1 = await StashManager.stash({ db: b1, description: "stash test 1" });

      chai.expect(stash1).to.exist;
      chai.assert(Guid.isGuid(stash1.id));
      chai.expect(stash1.description).to.equals("stash test 1");
      chai.expect(stash1.briefcaseId).equals(b1.briefcaseId);
      chai.expect(stash1.iModelId).to.equals(b1.iModelId);
      chai.expect(stash1.timestamp).to.exist;
      chai.expect(stash1.description).to.exist;
      chai.expect(stash1.hash).length(64);
      chai.expect(stash1.parentChangeset).to.exist;
      chai.expect(stash1.idSequences.element).to.equals("0x30000000004");
      chai.expect(stash1.idSequences.instance).to.equals("0x30000000000");
      chai.expect(stash1.acquiredLocks).equals(4);
      chai.expect(stash1.txns).to.exist;

      chai.expect(stash1.txns).to.have.lengthOf(4);
      chai.expect(stash1.txns[0].props.description).to.equal("first");
      chai.expect(stash1.txns[1].props.description).to.equal("second");
      chai.expect(stash1.txns[2].props.description).to.equal("third");
      chai.expect(stash1.txns[3].props.description).to.equal("fourth");

      chai.expect(stash1.txns[0].id).to.equals("0x100000000");
      chai.expect(stash1.txns[1].id).to.equals("0x100000001");
      chai.expect(stash1.txns[2].id).to.equals("0x100000002");
      chai.expect(stash1.txns[3].id).to.equals("0x100000003");

      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges(`fifth`);
      await testIModel.updateElement(b1Txn, e1);
      b1Txn.saveChanges(`sixth`);
      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges(`seventh`);

      const stash2 = await StashManager.stash({ db: b1, description: "stash test 2" });
      chai.expect(stash2).to.exist;
      chai.expect(stash2.description).to.equals("stash test 2");
      chai.expect(stash2.hash).length(64);
      chai.expect(stash2.parentChangeset).to.exist;
      chai.expect(stash2.idSequences.element).to.equals("0x30000000006");
      chai.expect(stash2.idSequences.instance).to.equals("0x30000000000");
      chai.expect(stash2.acquiredLocks).equals(4);
      chai.expect(stash2.txns).to.exist;

      chai.expect(stash2.txns).to.have.lengthOf(7);
      chai.expect(stash2.txns[0].props.description).to.equal("first");
      chai.expect(stash2.txns[1].props.description).to.equal("second");
      chai.expect(stash2.txns[2].props.description).to.equal("third");
      chai.expect(stash2.txns[3].props.description).to.equal("fourth");
      chai.expect(stash2.txns[4].props.description).to.equal("fifth");
      chai.expect(stash2.txns[5].props.description).to.equal("sixth");
      chai.expect(stash2.txns[6].props.description).to.equal("seventh");

      chai.expect(stash2.txns[0].id).to.equals("0x100000000");
      chai.expect(stash2.txns[1].id).to.equals("0x100000001");
      chai.expect(stash2.txns[2].id).to.equals("0x100000002");
      chai.expect(stash2.txns[3].id).to.equals("0x100000003");
      chai.expect(stash2.txns[4].id).to.equals("0x100000004");
      chai.expect(stash2.txns[5].id).to.equals("0x100000005");
      chai.expect(stash2.txns[6].id).to.equals("0x100000006");

      const stashes = StashManager.getStashes(b1);
      chai.expect(stashes).to.have.lengthOf(2);
      chai.expect(stashes[0].description).to.equals("stash test 2");
      chai.expect(stashes[1].description).to.equals("stash test 1");
      chai.expect(stashes[0]).to.deep.equal(stash2);
      chai.expect(stashes[1]).to.deep.equal(stash1);

      StashManager.dropAllStashes(b1);
      chai.expect(StashManager.getStashes(b1)).to.have.lengthOf(0);
    });
    it("recursively calling withIndirectTxnMode()", async () => {
      const b1 = await testIModel.openBriefcase();
      chai.expect(b1.txns.getMode()).to.equal("direct");
      b1.txns.withIndirectTxnMode(() => {
        chai.expect(b1.txns.getMode()).to.equal("indirect");
      });

      chai.expect(b1.txns.getMode()).to.equal("direct");

      b1.txns.withIndirectTxnMode(() => {
        chai.expect(b1.txns.getMode()).to.equal("indirect");
        b1.txns.withIndirectTxnMode(() => {
          chai.expect(b1.txns.getMode()).to.equal("indirect");
          b1.txns.withIndirectTxnMode(() => {
            chai.expect(b1.txns.getMode()).to.equal("indirect");
            b1.txns.withIndirectTxnMode(() => {
              chai.expect(b1.txns.getMode()).to.equal("indirect");
            });
            chai.expect(b1.txns.getMode()).to.equal("indirect");
          });
          chai.expect(b1.txns.getMode()).to.equal("indirect");
        });
      });

      chai.expect(b1.txns.getMode()).to.equal("direct");

      chai.expect(() =>
        b1.txns.withIndirectTxnMode(() => {
          chai.expect(b1.txns.getMode()).to.equal("indirect");
          throw new Error("Test error");
        })).to.throw();

      chai.expect(b1.txns.getMode()).to.equal("direct");
    });
    it("should fail to importSchemas() & importSchemaStrings() in indirect scope", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "indirect schema import failures");
      const schema = `<?xml version="1.0" encoding="UTF-8"?>
    <ECSchema schemaName="MySchema" alias="ms1" version="01.00.00" xmlns="http://www.bentley.com/schemas/Bentley.ECXML.3.2">
        <ECSchemaReference name="BisCore" version="01.00.00" alias="bis"/>
        <ECEntityClass typeName="my_class">
            <BaseClass>bis:GraphicalElement2d</BaseClass>
            <ECProperty propertyName="prop1" typeName="string" />
        </ECEntityClass>
    </ECSchema>`;

      const schemaFile = path.join(KnownTestLocations.outputDir, "MySchema.01.00.00.ecschema.xml");
      if (existsSync(schemaFile)) {
        unlinkSync(schemaFile);
      }
      writeFileSync(schemaFile, schema, { encoding: "utf8" });

      b1Txn.end("abandon");

      await chai.expect(b1.txns.withIndirectTxnModeAsync(async () => {
        await b1.importSchemas([schema]);
      })).to.be.rejectedWith("Cannot import schemas while in an indirect change scope");

      b1Txn.start();
      b1Txn.end("abandon");

      await chai.expect(b1.txns.withIndirectTxnModeAsync(async () => {
        await b1.importSchemaStrings([schema]);
      })).to.be.rejectedWith("Cannot import schemas while in an indirect change scope");

      await b1.importSchemaStrings([schema]);
      await b1.pushChanges({ description: "import schema" });
    });

    it("should fail to saveChanges() & pushChanges() in indirect scope", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "indirect save push failures");

      await testIModel.insertElement(b1Txn);

      await chai.expect(b1.txns.withIndirectTxnModeAsync(async () => {
        b1Txn.saveChanges();
      })).to.be.rejectedWith("Cannot save changes while in an indirect change scope");

      chai.expect(() => b1.txns.withIndirectTxnMode(() => {
        b1Txn.saveChanges();
      })).to.be.throws("Cannot save changes while in an indirect change scope");

      b1Txn.saveChanges();

      await chai.expect(b1.txns.withIndirectTxnModeAsync(async () => {
        await b1.pushChanges({ description: "test" });
      })).to.be.rejectedWith("Cannot pull and apply changeset while in an indirect change scope");

      await b1.pushChanges({ description: "test" });
    });

    it("should fail to saveFileProperty/deleteFileProperty in indirect scope", async () => {
      // pull/push/saveFileProperty/deleteFileProperty should be called inside indirect change scope.
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "indirect file property failures");
      b1Txn.saveFileProperty({ namespace: "test", name: "test" }, "Hello, World");

      await chai.expect(b1.txns.withIndirectTxnModeAsync(async () => {
        b1Txn.saveFileProperty({ namespace: "test", name: "test" }, "This should fail 1");
      })).to.be.rejectedWith("Cannot save file property while in an indirect change scope");

      chai.expect(b1.queryFilePropertyString({ namespace: "test", name: "test" })).to.equal("Hello, World");

      await chai.expect(b1.txns.withIndirectTxnModeAsync(async () => {
        b1Txn.deleteFileProperty({ namespace: "test", name: "test" });
      })).to.be.rejectedWith("Cannot save file property while in an indirect change scope");

      chai.expect(b1.queryFilePropertyString({ namespace: "test", name: "test" })).to.equal("Hello, World");

      chai.expect(() => b1.txns.withIndirectTxnMode(() => {
        b1Txn.saveFileProperty({ namespace: "test", name: "test" }, "This should fail 2");
      })).to.be.throws("Cannot save file property while in an indirect change scope");

      chai.expect(b1.queryFilePropertyString({ namespace: "test", name: "test" })).to.equal("Hello, World");

      chai.expect(() => b1.txns.withIndirectTxnMode(() => {
        b1Txn.deleteFileProperty({ namespace: "test", name: "test" });
      })).to.be.throws("Cannot save file property while in an indirect change scope");

      b1Txn.saveChanges();
    });

    it("recursively calling withIndirectTxnModeAsync()", async () => {
      const b1 = await testIModel.openBriefcase();
      chai.expect(b1.txns.getMode()).to.equal("direct");
      await b1.txns.withIndirectTxnModeAsync(async () => {
        chai.expect(b1.txns.getMode()).to.equal("indirect");
      });

      chai.expect(b1.txns.getMode()).to.equal("direct");

      await b1.txns.withIndirectTxnModeAsync(async () => {
        chai.expect(b1.txns.getMode()).to.equal("indirect");
        await b1.txns.withIndirectTxnModeAsync(async () => {
          chai.expect(b1.txns.getMode()).to.equal("indirect");
          await b1.txns.withIndirectTxnModeAsync(async () => {
            chai.expect(b1.txns.getMode()).to.equal("indirect");
            await b1.txns.withIndirectTxnModeAsync(async () => {
              chai.expect(b1.txns.getMode()).to.equal("indirect");
              await b1.txns.withIndirectTxnModeAsync(async () => {
                chai.expect(b1.txns.getMode()).to.equal("indirect");
              });
              chai.expect(b1.txns.getMode()).to.equal("indirect");
            });
            chai.expect(b1.txns.getMode()).to.equal("indirect");
          });
        });
        b1.txns.withIndirectTxnMode(() => {
          chai.expect(b1.txns.getMode()).to.equal("indirect");
        });
      });

      chai.expect(b1.txns.getMode()).to.equal("direct");
      await chai.expect(
        b1.txns.withIndirectTxnModeAsync(async () => {
          chai.expect(b1.txns.getMode()).to.equal("indirect");
          throw new Error("Test error");
        })).rejectedWith(Error);

      chai.expect(b1.txns.getMode()).to.equal("direct");
    });
    it("should restore mutually exclusive stashes", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "restore mutually exclusive stashes");

      // stash 1
      const e1 = await testIModel.insertElement(b1Txn);
      chai.expect(e1).to.exist;
      b1Txn.saveChanges("first");
      const stash1 = await StashManager.stash({ db: b1, description: "stash test 1", discardLocalChanges: true, retainLocks: true });
      chai.expect(stash1).to.exist;
      chai.expect(b1.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b1.txns.isUndoPossible).to.be.false;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      // stash 2
      const e2 = await testIModel.insertElement(b1Txn);
      chai.expect(e2).to.exist;
      b1Txn.saveChanges("second");
      const stash2 = await StashManager.stash({ db: b1, description: "stash test 2", discardLocalChanges: true, retainLocks: true });
      chai.expect(stash2).to.exist;
      chai.expect(b1.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e2)).to.undefined;
      chai.expect(b1.txns.isUndoPossible).to.be.false;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      // stash 3
      const e3 = await testIModel.insertElement(b1Txn);
      chai.expect(e3).to.exist;
      b1Txn.saveChanges("third");
      const stash3 = await StashManager.stash({ db: b1, description: "stash test 3", discardLocalChanges: true, retainLocks: true });
      chai.expect(stash3).to.exist;
      chai.expect(b1.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e2)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e3)).to.undefined;
      chai.expect(b1.txns.isUndoPossible).to.be.false;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      chai.expect(e1).not.equals(e2);
      chai.expect(e1).not.equals(e3);
      chai.expect(e2).not.equals(e3);

      const stashes = StashManager.getStashes(b1);
      chai.expect(stashes).to.have.lengthOf(3);
      chai.expect(stashes[0].description).to.equals("stash test 3");
      chai.expect(stashes[1].description).to.equals("stash test 2");
      chai.expect(stashes[2].description).to.equals("stash test 1");

      // restore stash 1
      await StashManager.restore({ db: b1, stash: stash1 });
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.elements.tryGetElement(e2)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e3)).to.undefined;

      // restore stash 2
      await StashManager.restore({ db: b1, stash: stash2 });
      chai.expect(b1.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e2)).to.exist;
      chai.expect(b1.elements.tryGetElement(e3)).to.undefined;

      // restore stash 3
      await StashManager.restore({ db: b1, stash: stash3 });
      chai.expect(b1.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e2)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e3)).to.exist;
    });
    it("should restore stash in any order", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "restore stash any order");

      // stash 1
      const e1 = await testIModel.insertElement(b1Txn);
      chai.expect(e1).to.exist;
      b1Txn.saveChanges("first");
      // do not discard local changes
      const stash1 = await StashManager.stash({ db: b1, description: "stash test 1" });
      chai.expect(stash1).to.exist;
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.txns.isUndoPossible).to.be.true;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      // stash 2
      const e2 = await testIModel.insertElement(b1Txn);
      chai.expect(e2).to.exist;
      b1Txn.saveChanges("second");
      // do not discard local changes
      const stash2 = await StashManager.stash({ db: b1, description: "stash test 2" });
      chai.expect(stash2).to.exist;
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.elements.tryGetElement(e2)).to.exist;
      chai.expect(b1.txns.isUndoPossible).to.be.true;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      // stash 3
      const e3 = await testIModel.insertElement(b1Txn);
      chai.expect(e3).to.exist;
      b1Txn.saveChanges("third");
      // do not discard local changes
      const stash3 = await StashManager.stash({ db: b1, description: "stash test 3" });
      chai.expect(stash3).to.exist;
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.elements.tryGetElement(e2)).to.exist;
      chai.expect(b1.elements.tryGetElement(e3)).to.exist;
      chai.expect(b1.txns.isUndoPossible).to.be.true;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      const stashes = StashManager.getStashes(b1);
      chai.expect(stashes).to.have.lengthOf(3);
      chai.expect(stashes[0].description).to.equals("stash test 3");
      chai.expect(stashes[1].description).to.equals("stash test 2");
      chai.expect(stashes[2].description).to.equals("stash test 1");

      await b1.discardChanges({ retainLocks: true });
      chai.expect(b1.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e2)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e3)).to.undefined;
      chai.expect(b1.txns.isUndoPossible).to.be.false;
      chai.expect(b1.txns.isRedoPossible).to.be.false;

      // restore stash 1
      await StashManager.restore({ db: b1, stash: stash1 });
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.elements.tryGetElement(e2)).to.undefined;
      chai.expect(b1.elements.tryGetElement(e3)).to.undefined;


      // restore stash 2
      await StashManager.restore({ db: b1, stash: stash2 });
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.elements.tryGetElement(e2)).to.exist;
      chai.expect(b1.elements.tryGetElement(e3)).to.undefined;

      // restore stash 3
      await StashManager.restore({ db: b1, stash: stash3 });
      chai.expect(b1.elements.tryGetElement(e1)).to.exist;
      chai.expect(b1.elements.tryGetElement(e2)).to.exist;
      chai.expect(b1.elements.tryGetElement(e3)).to.exist;

    });
    it("should restore stash when briefcase has advanced to latest changeset", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "restore stash latest changeset b1");
      const b2Txn = startTestTxn(b2, "restore stash latest changeset b2");

      chai.expect(b1.changeset.index).to.equals(2);
      chai.expect(b2.changeset.index).to.equals(2);

      const e1 = await testIModel.insertElement(b1Txn);
      chai.expect(e1).to.exist;
      b1Txn.saveChanges();
      await b1.pushChanges({ description: `${e1} inserted` });

      chai.expect(b1.changeset.index).to.equals(3);

      const e2 = await testIModel.insertElement(b2Txn);
      chai.expect(e2).to.exist;
      b2Txn.saveChanges();

      chai.expect(b2.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b2.elements.tryGetElement(e2)).to.exist;

      const b2Stash1 = await StashManager.stash({ db: b2, description: "stash test 1", discardLocalChanges: true });
      chai.expect(b2Stash1.parentChangeset.index).to.equals(2);

      chai.expect(b2.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b2.elements.tryGetElement(e2)).to.undefined;

      await b2.pullChanges();
      chai.expect(b2.changeset.index).to.equals(3);

      chai.expect(b2.elements.tryGetElement(e1)).to.exist;
      chai.expect(b2.elements.tryGetElement(e2)).to.undefined;

      // stash restore should downgrade briefcase to older changeset as specified in stash
      await StashManager.restore({ db: b2, stash: b2Stash1 });
      chai.expect(b2.changeset.index).to.equals(2);

      chai.expect(b2.elements.tryGetElement(e1)).to.undefined;
      chai.expect(b2.elements.tryGetElement(e2)).to.exist;

      await b2.pullChanges();
      chai.expect(b2.changeset.index).to.equals(3);
      chai.expect(b2.elements.tryGetElement(e1)).to.exist;
      chai.expect(b2.elements.tryGetElement(e2)).to.exist;

      await b2.pushChanges({ description: "test" });
      chai.expect(b2.changeset.index).to.equals(4);
    });
    it("restore stash that has element changed by another briefcase", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "restore stash changed element b1");
      const b2Txn = startTestTxn(b2, "restore stash changed element b2");

      chai.expect(b1.changeset.index).to.equals(2);
      chai.expect(b2.changeset.index).to.equals(2);

      const e1 = await testIModel.insertElement(b1Txn);
      chai.expect(e1).to.exist;
      b1Txn.saveChanges();
      await b1.pushChanges({ description: `${e1} inserted` });

      chai.expect(b1.changeset.index).to.equals(3);

      await b2.pullChanges();
      chai.expect(b2.changeset.index).to.equals(3);
      await testIModel.updateElement(b2Txn, e1);
      b2Txn.saveChanges();

      chai.expect(b2.locks.holdsExclusiveLock(e1)).to.be.true;
      const b2Stash1 = await StashManager.stash({ db: b2, description: "stash test 1", discardLocalChanges: true });
      chai.expect(b2Stash1.parentChangeset.index).to.equals(3);
      chai.expect(b2.locks.holdsExclusiveLock(e1)).to.be.false;

      // stash release lock so b2 should have released lock and b1 should be able to update.
      await testIModel.updateElement(b1Txn, e1);
      b1Txn.saveChanges();

      // restore stash should fail because of lock not obtained on e1
      await chai.expect(StashManager.restore({ db: b2, stash: b2Stash1 })).to.be.rejectedWith("exclusive lock is already held");

      // push b1 changes to release lock
      await b1.pushChanges({ description: `${e1} inserted` });

      // restore stash should fail because pull is required to obtain lock
      await chai.expect(StashManager.restore({ db: b2, stash: b2Stash1 })).to.be.rejectedWith("pull is required to obtain lock");

      await b2.pullChanges();

      chai.expect(b2.changeset.index).to.equals(4);
      const elBefore = b2.elements.tryGetElementProps(e1);
      chai.expect((elBefore as any).prop1).to.equals("3");
      // restore stash should succeed as now it can obtain lock
      await StashManager.restore({ db: b2, stash: b2Stash1 });

      const elAfter = b2.elements.tryGetElementProps(e1);
      chai.expect((elAfter as any).prop1).to.equals("2");
      await b2.pushChanges({ description: `${e1} updated` });
    });
    it("schema change should not be stashed", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "schema change should not be stashed");
      const schema1 = `<?xml version="1.0" encoding="UTF-8"?>
        <ECSchema schemaName="TestDomain" alias="ts" version="01.00.01" xmlns="http://www.bentley.com/schemas/Bentley.ECXML.3.2">
            <ECSchemaReference name="BisCore" version="01.00.00" alias="bis"/>
            <ECEntityClass typeName="a1">
                <BaseClass>bis:GraphicalElement2d</BaseClass>
                <ECProperty propertyName="prop1" typeName="string" />
                <ECProperty propertyName="prop2" typeName="string" />
            </ECEntityClass>
            <ECEntityClass typeName="A1Recipe2d">
                <BaseClass>bis:TemplateRecipe2d</BaseClass>
                <ECProperty propertyName="prop1" typeName="string" />
            </ECEntityClass>
            <ECRelationshipClass typeName="A1OwnsA1" modifier="None" strength="embedding">
                <BaseClass>bis:ElementOwnsChildElements</BaseClass>
                <Source multiplicity="(0..1)" roleLabel="owns" polymorphic="true">
                    <Class class="a1"/>
                </Source>
                <Target multiplicity="(0..*)" roleLabel="is owned by" polymorphic="false">
                    <Class class="a1"/>
                </Target>
            </ECRelationshipClass>
        </ECSchema>`;
      await importSchemaStrings(b1Txn, [schema1]);

      await chai.expect(StashManager.stash({ db: b1, description: "stash test 1" })).to.not.rejectedWith("Bad Arg: Pending schema changeset stashing is not currently supported");
    });
    it("abort rebase", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "abort rebase b1");
      const b2Txn = startTestTxn(b2, "abort rebase b2");

      const e1 = await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: `${e1} inserted` });

      const e2 = await testIModel.insertElement(b2Txn);
      chai.expect(e2).to.exist;
      let e3 = "";
      b2Txn.saveChanges();
      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txnProps: TxnProps) => {
          return true;
        },
        recompute: async (_txnProps: TxnProps) => {
          chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.true;
          e3 = await testIModel.insertElement(b2Txn);
          throw new Error("Rebase failed");
        },
      });

      chai.expect(b2.elements.tryGetElementProps(e1)).to.undefined;
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined;
      chai.expect(b2.changeset.index).to.equals(2);
      await chai.expect(b2.pullChanges()).to.be.rejectedWith("Rebase failed");

      chai.expect(b2.changeset.index).to.equals(3);
      chai.expect(e3).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e1)).to.exist;     // came from incoming changeset
      chai.expect(b2.elements.tryGetElementProps(e2)).to.undefined; // was local change and reversed during rebase.
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined; // was insert by reCompute() but due to exception the rebase attempt was abandoned.

      chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.true;

      chai.expect(b2.txns.rebaser.canAbort()).is.true;
      await b2.txns.rebaser.abort();

      chai.expect(b2.changeset.index).to.equals(2);
      chai.expect(b2.elements.tryGetElementProps(e1)).to.undefined; // reset briefcase should move tip back to where it was before pull
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;  // abort should put back e2 which was only change at the time of pull
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined; // add by rebase so should not exist either

      chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.false;
    });
    it("calling discardChanges() from inside indirect scope is not allowed", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "discard changes inside indirect scope");
      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges();
      const p1 = b1.txns.withIndirectTxnModeAsync(async () => {
        await b1.discardChanges();
      });
      await chai.expect(p1).to.be.rejectedWith("Cannot discard changes when there are indirect changes");
      b1Txn.saveChanges();
    });
    it("calling discardChanges() during rebasing is not allowed", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "discard changes during rebasing b1");
      const b2Txn = startTestTxn(b2, "discard changes during rebasing b2");
      await testIModel.insertElement(b1Txn);
      await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "inserted element" });

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn);
      b2Txn.saveChanges();

      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txnProps: TxnProps) => {
          return true;
        },
        recompute: async (_txnProps: TxnProps) => {
          chai.expect(b2.txns.rebaser.isAborting).is.false;
          await b2.discardChanges();
        },
      });

      chai.expect(b2.txns.rebaser.isAborting).is.false;
      const p1 = b2.pullChanges();
      await chai.expect(p1).to.be.rejectedWith("Cannot discard changes while a rebase is in progress");
      chai.expect(b2.txns.rebaser.canAbort()).is.true;
      await b2.txns.rebaser.abort();
    });
    it("getStash() should throw exception", async () => {
      const b1 = await testIModel.openBriefcase();
      chai.expect(() => StashManager.getStash({ db: b1, stash: "invalid_stash" })).to.throw("No stashes exist for this briefcase");
      chai.expect(StashManager.tryGetStash({ db: b1, stash: "invalid_stash" })).to.be.undefined;
    });
    it("edge case: a indirect update can cause FK violation", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "indirect update fk violation b1");
      const b2Txn = startTestTxn(b2, "indirect update fk violation b2");

      const parentId = await testIModel.insertElement(b1Txn);
      const childId = await testIModel.insertElementEx(b1Txn, { parent: { id: parentId, relClassName: "TestDomain:A1OwnsA1" } });
      b1Txn.saveChanges("insert parent and child");
      await b1.pushChanges({ description: `inserted parent ${parentId} and child ${childId}` });
      await b2.pullChanges();

      // b1 delete childId while b1 create a child of childId as indirect change
      await testIModel.deleteElement(b1Txn, childId);
      b1Txn.saveChanges("delete child");
      // no exclusive lock required on child1
      const grandChildId = await testIModel.insertElementEx(b2Txn, { parent: { id: childId, relClassName: "TestDomain:A1OwnsA1" }, markAsIndirect: true });
      b2Txn.saveChanges("delete child and insert grandchild");

      await b1.pushChanges({ description: `deleted child ${childId}` });

      // should fail to pull and rebase changes.
      await chai.expect(b2.pushChanges({ description: `deleted child ${childId} and inserted grandchild ${grandChildId}` }))
        .to.be.rejectedWith("Foreign key conflicts in ChangeSet. Aborting rebase.");
    });

    it("ECSqlReader unable to read updates after saveChanges()", async () => {
      const b1 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "ecsql reader updates after saveChanges");
      const findElement = async (id: Id64String) => {
        const reader = b1.createQueryReader(`SELECT ECInstanceId, ec_className(ECClassId), Prop1 FROM ts.A1 WHERE ECInstanceId = ${id}`, QueryBinder.from([id]));
        if (await reader.step())
          return { id: reader.current[0], className: reader.current[1], prop1: reader.current[2] };
        return undefined;
      }

      const runQuery = async (query: string) => {
        const reader = b1.createQueryReader(query);
        let rows = 0;
        while (await reader.step()) {
          rows++;
        }
        return rows;
      }
      const runQueryParallel = async (query: string, times: number = 1) => {
        return Promise.all(new Array(times).fill(query).map(runQuery));
      }

      // Following query have open cached statement against BisCore.Element that will prevent
      // updates from being visible until the statement is finalized.
      await runQueryParallel(`SELECT $ FROM BisCore.Element`, 10);

      const e1 = await testIModel.insertElement(b1Txn);
      chai.expect(await findElement(e1)).to.be.undefined;
      b1Txn.saveChanges("insert element");

      const e1Props = await findElement(e1);
      chai.expect(e1Props).to.exist;
      await runQueryParallel(`SELECT $ FROM BisCore.Element`, 10);
      const e2 = await testIModel.insertElement(b1Txn);
      chai.expect(await findElement(e2)).to.be.undefined;
      b1Txn.saveChanges("insert second element");

      const e2Props = await findElement(e2);
      chai.expect(e2Props).to.exist;

      await runQueryParallel(`SELECT $ FROM BisCore.Element`, 10);
      const e3 = await testIModel.insertElement(b1Txn);
      chai.expect(await findElement(e3)).to.be.undefined;
      b1Txn.saveChanges("insert third element");

      const e3Props = await findElement(e3);
      chai.expect(e3Props).to.exist;
    });
    it("enum txn changes in recompute", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "enum txn changes in recompute b1");
      const b2Txn = startTestTxn(b2, "enum txn changes in recompute b2");

      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });

      await b2.pullChanges();

      await testIModel.updateElement(b1Txn, e1);
      await testIModel.updateElement(b1Txn, e2, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "update element 1 direct and 1 indirect" });


      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("first change");

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("second change");

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("third change");

      let txnVerified = 0;
      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (txn: TxnProps): Promise<void> => {
          const reader = SqliteChangesetReader.openTxn({ txnId: txn.id, db: b2, disableSchemaCheck: true });
          const adaptor = new ChangesetECAdaptor(reader); // eslint-disable-line @typescript-eslint/no-deprecated
          adaptor.acceptClass("TestDomain:a1");
          const ids = new Set<Id64String>();
          while (adaptor.step()) {
            if (!adaptor.reader.isIndirect)
              ids.add(adaptor.inserted?.ECInstanceId || adaptor.deleted?.ECInstanceId as Id64String);
          }
          adaptor.close();

          if (txn.props.description === "first change") {
            chai.expect(Array.from(ids.keys())).deep.equal(["0x40000000001"]);
            txnVerified++;
          } else if (txn.props.description === "second change") {
            chai.expect(Array.from(ids.keys())).deep.equal(["0x40000000003"]);
            txnVerified++;
          } else if (txn.props.description === "third change") {
            chai.expect(Array.from(ids.keys())).deep.equal(["0x40000000005"]);
            txnVerified++;
          } else {
            txnVerified++;
          }
        },
      });
      await b2.pullChanges();
      chai.expect(txnVerified).to.equal(3);
    });

    it("before and after rebase events", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "before and after rebase events b1");
      const b2Txn = startTestTxn(b2, "before and after rebase events b2");

      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });

      await b2.pullChanges();

      await testIModel.updateElement(b1Txn, e1);
      await testIModel.updateElement(b1Txn, e2, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "update element 1 direct and 1 indirect" });


      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("first change");

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("second change");

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("third change");

      const events = {
        onRebase: {
          beginCount: 0,
          endCount: 0,
          beginTxns: [] as TxnProps[],
        },
        onRebaseTxn: {
          beginTxns: [] as TxnProps[],
          endTxns: [] as TxnProps[],
        },
        rebaseHandler: {
          shouldReinstate: [] as TxnProps[],
          recompute: [] as TxnProps[],
        },
        pullMerge: {
          beginCount: 0,
          endCount: 0,
          beginChangeset: [] as ChangesetIdWithIndex[],
          endChangeset: [] as ChangesetIdWithIndex[],
        },
        applyIncomingChanges: {
          beginCount: 0,
          endCount: 0,
          beginChangesets: [] as ChangesetProps[],
          endChangesets: [] as ChangesetProps[],
        },
        reverseLocalChanges: {
          beginCount: 0,
          endCount: 0,
          txns: [] as TxnProps[],
        },
        downloadChangesets: {
          beginCount: 0,
          endCount: 0,
        },
      };

      const resetEvent = () => {
        events.onRebase.beginCount = 0;
        events.onRebase.endCount = 0;
        events.onRebase.beginTxns = [];
        events.onRebaseTxn.beginTxns = [];
        events.onRebaseTxn.endTxns = [];
        events.rebaseHandler.shouldReinstate = [];
        events.rebaseHandler.recompute = [];
        events.pullMerge.beginCount = 0;
        events.pullMerge.endCount = 0;
        events.pullMerge.beginChangeset = [];
        events.pullMerge.endChangeset = [];
        events.applyIncomingChanges.beginCount = 0;
        events.applyIncomingChanges.endCount = 0;
        events.applyIncomingChanges.beginChangesets = [];
        events.applyIncomingChanges.endChangesets = [];
        events.reverseLocalChanges.beginCount = 0;
        events.reverseLocalChanges.endCount = 0;
        events.reverseLocalChanges.txns = [];
        events.downloadChangesets.beginCount = 0;
        events.downloadChangesets.endCount = 0;
      };

      // onPullMergeXXXX
      b2.txns.rebaser.onPullMergeBegin.addListener((changeset: ChangesetIdWithIndex) => {
        events.pullMerge.beginCount++;
        events.pullMerge.beginChangeset.push(changeset);
      });

      b2.txns.rebaser.onPullMergeEnd.addListener((changeset: ChangesetIdWithIndex) => {
        events.pullMerge.endCount++;
        events.pullMerge.endChangeset.push(changeset);
      });

      // onApplyIncomingChangesXXXX
      b2.txns.rebaser.onApplyIncomingChangesBegin.addListener((changesets: ChangesetProps[]) => {
        events.applyIncomingChanges.beginCount++;
        events.applyIncomingChanges.beginChangesets.push(...changesets);
      });


      b2.txns.rebaser.onApplyIncomingChangesEnd.addListener((changesets: ChangesetProps[]) => {
        events.applyIncomingChanges.endCount++;
        events.applyIncomingChanges.endChangesets.push(...changesets);
      });

      // onReverseLocalChangesXXXX
      b2.txns.rebaser.onReverseLocalChangesBegin.addListener(() => {
        events.reverseLocalChanges.beginCount++;
      });

      b2.txns.rebaser.onReverseLocalChangesEnd.addListener((txns: TxnProps[]) => {
        events.reverseLocalChanges.endCount++;
        removePropertyRecursive(txns, "timestamp"); // it changes on each run, so remove it for comparison
        events.reverseLocalChanges.txns.push(...txns);
      });

      // onDownloadChangesetsXXXX
      b2.txns.rebaser.onDownloadChangesetsBegin.addListener(() => {
        events.downloadChangesets.beginCount++;
      });

      b2.txns.rebaser.onDownloadChangesetsEnd.addListener(() => {
        events.downloadChangesets.endCount++;
      });

      // onRebaseXXXX
      b2.txns.rebaser.onRebaseBegin.addListener((txns: TxnProps[]) => {
        events.onRebase.beginCount++;
        removePropertyRecursive(txns, "timestamp"); // it changes on each run, so remove it for comparison
        events.onRebase.beginTxns.push(...txns);
      });

      b2.txns.rebaser.onRebaseEnd.addListener(() => {
        events.onRebase.endCount++;
      });

      // onRebaseTxnXXXX
      b2.txns.rebaser.onRebaseTxnBegin.addListener((txn: TxnProps) => {
        removePropertyRecursive(txn, "timestamp"); // it changes on each run, so remove it for comparison
        events.onRebaseTxn.beginTxns.push(txn);
      });

      b2.txns.rebaser.onRebaseTxnEnd.addListener((txn: TxnProps) => {
        removePropertyRecursive(txn, "timestamp"); // it changes on each run, so remove it for comparison
        events.onRebaseTxn.endTxns.push(txn);
      });

      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          // shouldReinstate
          removePropertyRecursive(_txn, "timestamp"); // it changes on each run, so remove it for comparison
          events.rebaseHandler.shouldReinstate.push(_txn);
          return true;
        },
        recompute: async (_txn: TxnProps): Promise<void> => {
          // recompute
          removePropertyRecursive(_txn, "timestamp"); // it changes on each run, so remove it for comparison
          events.rebaseHandler.recompute.push(_txn);
        },
      });

      resetEvent();
      await b2.pullChanges();
      // pullMerge events
      chai.expect(events.pullMerge.beginCount).to.equal(1);
      chai.expect(events.pullMerge.endCount).to.equal(1);
      chai.expect((events.pullMerge.beginChangeset[0].index)).to.equal(3);
      chai.expect((events.pullMerge.endChangeset[0].index)).to.equal(4);

      // applyIncomingChanges events
      chai.expect(events.applyIncomingChanges.beginCount).to.equal(1);
      chai.expect(events.applyIncomingChanges.endCount).to.equal(1);
      chai.expect(events.applyIncomingChanges.beginChangesets.map((cs) => cs.index)).to.deep.equal([4]);
      chai.expect(events.applyIncomingChanges.endChangesets.map((cs) => cs.index)).to.deep.equal([4]);

      // downloadChangesets events
      chai.expect(events.downloadChangesets.beginCount).to.equal(1);
      chai.expect(events.downloadChangesets.endCount).to.equal(1);

      // reverseLocalChanges events
      chai.expect(events.reverseLocalChanges.beginCount).to.equal(1);
      chai.expect(events.reverseLocalChanges.endCount).to.equal(1);
      chai.expect(events.reverseLocalChanges.txns.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002"]);

      // rebase events
      chai.expect(events.onRebase.beginCount).to.equal(1);
      chai.expect(events.onRebase.endCount).to.equal(1);
      chai.expect(events.onRebase.beginTxns).to.deep.equal(
        [
          {
            grouped: false,
            id: "0x100000000",
            nextId: "0x100000001",
            props: {
              description: "first change"
            },
            reversed: true,
            sessionId: 1,
            type: "Data"
          },
          {
            grouped: false,
            id: "0x100000001",
            nextId: "0x100000002",
            prevId: "0x100000000",
            props: {
              description: "second change",
            },
            reversed: true,
            sessionId: 1,
            type: "Data"
          },
          {
            grouped: false,
            id: "0x100000002",
            prevId: "0x100000001",
            props: {
              description: "third change"
            },
            reversed: true,
            sessionId: 1,
            type: "Data"
          }
        ]
      );

      chai.expect(events.onRebaseTxn.beginTxns.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002"]);
      chai.expect(events.onRebaseTxn.endTxns.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002"]);

      chai.expect(events.rebaseHandler.shouldReinstate.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002"]);
      chai.expect(events.rebaseHandler.recompute.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002"]);

      await testIModel.updateElement(b1Txn, e1);
      await testIModel.updateElement(b1Txn, e2, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "update element 1 direct and 1 indirect" });

      await testIModel.insertElement(b2Txn);
      await testIModel.insertElement(b2Txn, true);
      b2Txn.saveChanges("fourth change");

      resetEvent();
      await b2.pullChanges();

      chai.expect(events.onRebase.beginCount).to.equal(1);
      chai.expect(events.onRebase.endCount).to.equal(1);
      chai.expect(events.onRebase.beginTxns).to.deep.equal([
        {
          grouped: false,
          id: "0x100000000",
          nextId: "0x100000001",
          props: {
            description: "first change"
          },
          reversed: true,
          sessionId: 1,
          type: "Data"
        },
        {
          grouped: false,
          id: "0x100000001",
          nextId: "0x100000002",
          prevId: "0x100000000",
          props: {
            description: "second change"
          },
          reversed: true,
          sessionId: 1,
          type: "Data"
        },
        {
          grouped: false,
          id: "0x100000002",
          nextId: "0x100000003",
          prevId: "0x100000001",
          props: {
            description: "third change"
          },
          reversed: true,
          sessionId: 1,
          type: "Data"
        },
        {
          grouped: false,
          id: "0x100000003",
          prevId: "0x100000002",
          props: {
            description: "fourth change"
          },
          reversed: true,
          sessionId: 1,
          type: "Data"
        }
      ]);
      chai.expect(events.onRebaseTxn.beginTxns.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002", "0x100000003"]);
      chai.expect(events.onRebaseTxn.endTxns.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002", "0x100000003"]);

      chai.expect(events.rebaseHandler.shouldReinstate.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002", "0x100000003"]);
      chai.expect(events.rebaseHandler.recompute.map((txn) => txn.id)).to.deep.equal(["0x100000000", "0x100000001", "0x100000002", "0x100000003"]);
    });
    it("onModelGeometryChanged() not fired during rebase/pullMerge with no local change", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b2Txn = startTestTxn(b2, "model geometry changed no local change b2");

      const pushChangeFromB2 = async () => {
        await b2.pullChanges();
        await testIModel.insertElement(b2Txn);
        b2Txn.saveChanges();
        await b2.pushChanges({ description: "insert element on b2" });
      };

      const events = {
        modelGeometryChanged: [] as ReadonlyArray<ModelIdAndGeometryGuid>[],
      };

      const getGeometryGuidFromB1 = (modelId: string) => {
        const modelProps = b1.models.tryGetModelProps<GeometricModelProps>(modelId);
        return modelProps?.geometryGuid;
      };

      const clearEvents = () => {
        events.modelGeometryChanged = [];
      };

      b1.txns.onModelGeometryChanged.addListener((changes: ReadonlyArray<ModelIdAndGeometryGuid>) => {
        events.modelGeometryChanged.push(changes);
      });

      clearEvents();

      b1.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (_txn: TxnProps) => {
        },
      });

      await pushChangeFromB2();

      clearEvents();
      const geomGuidBeforePull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidBeforePull).is.undefined;
      await b1.pushChanges({ description: "push changes on b1" });
      const geomGuidAfterPull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidAfterPull).is.undefined;
      chai.expect(events.modelGeometryChanged.length).to.equal(0);
    });
    it("onModelGeometryChanged() fired during rebase with geometric local change", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "model geometry changed geometric local change b1");
      const b2Txn = startTestTxn(b2, "model geometry changed geometric local change b2");

      const pushChangeFromB2 = async () => {
        await b2.pullChanges();
        await testIModel.insertElement(b2Txn);
        b2Txn.saveChanges();
        await b2.pushChanges({ description: "insert element on b2" });
      };

      const events = {
        modelGeometryChanged: [] as ReadonlyArray<ModelIdAndGeometryGuid>[],
        onGeometryChanged: [] as ModelGeometryChangesProps[][],
      };

      const getGeometryGuidFromB1 = (modelId: string) => {
        const modelProps = b1.models.tryGetModelProps<GeometricModelProps>(modelId);
        return modelProps?.geometryGuid;
      };

      const clearEvents = () => {
        events.modelGeometryChanged = [];
        events.onGeometryChanged = [];
      };

      b1.txns.onModelGeometryChanged.addListener((changes: ReadonlyArray<ModelIdAndGeometryGuid>) => {
        events.modelGeometryChanged.push(changes);
      });
      b1.txns.onGeometryChanged.addListener((changes: ModelGeometryChangesProps[]) => {
        events.onGeometryChanged.push(changes);
      });

      clearEvents();
      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn, true);
      chai.expect(e1).to.exist;
      chai.expect(e2).to.exist;
      b1Txn.saveChanges(`insert element ${e1} and ${e2}`);

      chai.expect(events.modelGeometryChanged.length).to.equal(1);
      chai.expect(events.modelGeometryChanged[0].length).to.equal(1);
      chai.expect(events.modelGeometryChanged[0][0].id).to.equal("0x20000000001");
      chai.assert(Guid.isGuid(events.modelGeometryChanged[0][0].guid));

      b1.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (_txn: TxnProps) => {
          await testIModel.updateElement(b1Txn, e1);
          await testIModel.updateElement(b1Txn, e2);
        },
      });

      await pushChangeFromB2();

      clearEvents();
      const geomGuidBeforePull = getGeometryGuidFromB1("0x20000000001");
      await b1.pushChanges({ description: "push changes on b1" });
      const geomGuidAfterPull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidBeforePull).to.not.equal(geomGuidAfterPull);
      chai.expect(events.modelGeometryChanged.length).to.equal(4);
    });
    it("onModelGeometryChanged() fired during rebase with non-geometric local change", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "model geometry changed non geometric local change b1");
      const b2Txn = startTestTxn(b2, "model geometry changed non geometric local change b2");

      const pushChangeFromB2 = async () => {
        await b2.pullChanges();
        await testIModel.insertElement(b2Txn);
        b2Txn.saveChanges();
        await b2.pushChanges({ description: "insert element on b2" });
      };

      const events = {
        modelGeometryChanged: [] as ReadonlyArray<ModelIdAndGeometryGuid>[],
      };

      const getGeometryGuidFromB1 = (modelId: string) => {
        const modelProps = b1.models.tryGetModelProps<GeometricModelProps>(modelId);
        return modelProps?.geometryGuid;
      };

      const clearEvents = () => {
        events.modelGeometryChanged = [];
      };

      b1.txns.onModelGeometryChanged.addListener((changes: ReadonlyArray<ModelIdAndGeometryGuid>) => {
        events.modelGeometryChanged.push(changes);
      });

      clearEvents();

      b1.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (_txn: TxnProps) => {
        },
      });

      await pushChangeFromB2();
      await testIModel.insertRecipe2d(b1Txn);
      b1Txn.saveChanges();

      clearEvents();
      const geomGuidBeforePull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidBeforePull).is.undefined;
      await b1.pushChanges({ description: "push changes on b1" });
      const geomGuidAfterPull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidAfterPull).to.exist;
      chai.expect(events.modelGeometryChanged.length).to.equal(1);
    });
    it("onModelGeometryChanged() fired during rebase with geometric local change", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "model geometry changed geometric local recipe b1");
      const b2Txn = startTestTxn(b2, "model geometry changed geometric local recipe b2");

      const pushChangeFromB2 = async () => {
        await b2.pullChanges();
        await testIModel.insertRecipe2d(b2Txn);
        b2Txn.saveChanges();
        await b2.pushChanges({ description: "insert element on b2" });
      };

      const events = {
        modelGeometryChanged: [] as ReadonlyArray<ModelIdAndGeometryGuid>[],
        onGeometryChanged: [] as ModelGeometryChangesProps[][],
      };

      const getGeometryGuidFromB1 = (modelId: string) => {
        const modelProps = b1.models.tryGetModelProps<GeometricModelProps>(modelId);
        return modelProps?.geometryGuid;
      };

      const clearEvents = () => {
        events.modelGeometryChanged = [];
        events.onGeometryChanged = [];
      };

      b1.txns.onModelGeometryChanged.addListener((changes: ReadonlyArray<ModelIdAndGeometryGuid>) => {
        events.modelGeometryChanged.push(changes);
      });

      b1.txns.onGeometryChanged.addListener((changes: ModelGeometryChangesProps[]) => {
        events.onGeometryChanged.push(changes);
      });

      clearEvents();

      b1.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (_txn: TxnProps) => {
          await testIModel.insertElement(b1Txn);
        },
      });

      await pushChangeFromB2();

      clearEvents();
      const geomGuidBeforePull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidBeforePull).is.undefined;
      await b1.pushChanges({ description: "push changes on b1" });
      const geomGuidAfterPull = getGeometryGuidFromB1("0x20000000001");
      chai.expect(geomGuidAfterPull).is.undefined;
      chai.expect(events.modelGeometryChanged.length).to.equal(0);
    });
    it("rebase multi txn", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "rebase multi txn b1");
      const b2Txn = startTestTxn(b2, "rebase multi txn b2");

      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "insert element 1 direct and 1 indirect" });

      await b2.pullChanges();

      chai.expect(b2.txns.beginMultiTxnOperation()).to.be.equals(DbResult.BE_SQLITE_OK);
      let elId = await testIModel.insertElement(b2Txn);
      b2Txn.saveChanges(`insert element ${elId}`);
      elId = await testIModel.insertElement(b2Txn);
      b2Txn.saveChanges(`insert element ${elId}`);
      elId = await testIModel.insertElement(b2Txn);
      b2Txn.saveChanges(`insert element ${elId}`);
      chai.expect(b2.txns.endMultiTxnOperation()).to.be.equals(DbResult.BE_SQLITE_OK);
      b2Txn.saveChanges();
      elId = await testIModel.insertElement(b2Txn);
      b2Txn.saveChanges(`insert element ${elId}`);
      let txns = Array.from(b2.txns.queryTxns());

      chai.expect(txns[0].id).to.be.equals("0x100000000"); // 1st after beginMultiTxnOperation()
      chai.expect(txns[0].props.description).to.be.equals("insert element 0x40000000001");
      chai.expect(txns[0].sessionId).to.be.equals(1);
      chai.expect(txns[0].grouped).to.be.equals(false);
      chai.expect(txns[0].reversed).to.be.equals(false);

      chai.expect(txns[1].id).to.be.equals("0x100000001"); // 2nd after beginMultiTxnOperation()
      chai.expect(txns[1].props.description).to.be.equals("insert element 0x40000000002");
      chai.expect(txns[1].sessionId).to.be.equals(1);
      chai.expect(txns[1].grouped).to.be.equals(true);
      chai.expect(txns[1].reversed).to.be.equals(false);

      chai.expect(txns[2].id).to.be.equals("0x100000002"); // 3rd after beginMultiTxnOperation() & before endMultiTxnOperation()
      chai.expect(txns[2].props.description).to.be.equals("insert element 0x40000000003");
      chai.expect(txns[2].sessionId).to.be.equals(1);
      chai.expect(txns[2].grouped).to.be.equals(true);
      chai.expect(txns[2].reversed).to.be.equals(false);

      chai.expect(txns[3].id).to.be.equals("0x100000003"); // 4th after endMultiTxnOperation()
      chai.expect(txns[3].props.description).to.be.equals("insert element 0x40000000004");
      chai.expect(txns[3].type).to.be.equals("Data");
      chai.expect(txns[3].grouped).to.be.equals(false);
      chai.expect(txns[3].reversed).to.be.equals(false);

      // reverse single txn 0x100000003
      chai.expect(b2.txns.reverseSingleTxn()).to.be.equals(DbResult.BE_SQLITE_OK);
      txns = Array.from(b2.txns.queryTxns());

      chai.expect(txns[0].id).to.be.equals("0x100000000"); // 1st after beginMultiTxnOperation()
      chai.expect(txns[0].props.description).to.be.equals("insert element 0x40000000001");
      chai.expect(txns[0].sessionId).to.be.equals(1);
      chai.expect(txns[0].grouped).to.be.equals(false);
      chai.expect(txns[0].reversed).to.be.equals(false);

      chai.expect(txns[1].id).to.be.equals("0x100000001"); // 2nd after beginMultiTxnOperation()
      chai.expect(txns[1].props.description).to.be.equals("insert element 0x40000000002");
      chai.expect(txns[1].sessionId).to.be.equals(1);
      chai.expect(txns[1].grouped).to.be.equals(true);
      chai.expect(txns[1].reversed).to.be.equals(false);

      chai.expect(txns[2].id).to.be.equals("0x100000002"); // 3rd after beginMultiTxnOperation() & before endMultiTxnOperation()
      chai.expect(txns[2].props.description).to.be.equals("insert element 0x40000000003");
      chai.expect(txns[2].sessionId).to.be.equals(1);
      chai.expect(txns[2].grouped).to.be.equals(true);
      chai.expect(txns[2].reversed).to.be.equals(false);

      chai.expect(txns[3].id).to.be.equals("0x100000003"); // 4th after endMultiTxnOperation()
      chai.expect(txns[3].props.description).to.be.equals("insert element 0x40000000004");
      chai.expect(txns[3].type).to.be.equals("Data");
      chai.expect(txns[3].grouped).to.be.equals(false);
      chai.expect(txns[3].reversed).to.be.equals(true);

      // reverse multi txn. should reverse 0x100000000, 0x100000001 & 0x100000002
      chai.expect(b2.txns.reverseSingleTxn()).to.be.equals(DbResult.BE_SQLITE_OK);
      txns = Array.from(b2.txns.queryTxns());

      chai.expect(txns[0].id).to.be.equals("0x100000000"); // 1st after beginMultiTxnOperation()
      chai.expect(txns[0].props.description).to.be.equals("insert element 0x40000000001");
      chai.expect(txns[0].sessionId).to.be.equals(1);
      chai.expect(txns[0].grouped).to.be.equals(false);
      chai.expect(txns[0].reversed).to.be.equals(true);

      chai.expect(txns[1].id).to.be.equals("0x100000001"); // 2nd after beginMultiTxnOperation()
      chai.expect(txns[1].props.description).to.be.equals("insert element 0x40000000002");
      chai.expect(txns[1].sessionId).to.be.equals(1);
      chai.expect(txns[1].grouped).to.be.equals(true);
      chai.expect(txns[1].reversed).to.be.equals(true);

      chai.expect(txns[2].id).to.be.equals("0x100000002"); // 3rd after beginMultiTxnOperation() & before endMultiTxnOperation()
      chai.expect(txns[2].props.description).to.be.equals("insert element 0x40000000003");
      chai.expect(txns[2].sessionId).to.be.equals(1);
      chai.expect(txns[2].grouped).to.be.equals(true);
      chai.expect(txns[2].reversed).to.be.equals(true);

      chai.expect(txns[3].id).to.be.equals("0x100000003"); // 4th after endMultiTxnOperation()
      chai.expect(txns[3].props.description).to.be.equals("insert element 0x40000000004");
      chai.expect(txns[3].type).to.be.equals("Data");
      chai.expect(txns[3].grouped).to.be.equals(false);
      chai.expect(txns[3].reversed).to.be.equals(true);

      // reinstate the transaction
      chai.expect(b2.txns.isRedoPossible).to.be.equals(true);
      chai.expect(b2.txns.reinstateTxn()).to.be.equals(DbResult.BE_SQLITE_OK);

      txns = Array.from(b2.txns.queryTxns());

      chai.expect(txns[0].id).to.be.equals("0x100000000"); // 1st after beginMultiTxnOperation()
      chai.expect(txns[0].props.description).to.be.equals("insert element 0x40000000001");
      chai.expect(txns[0].sessionId).to.be.equals(1);
      chai.expect(txns[0].grouped).to.be.equals(false);
      chai.expect(txns[0].reversed).to.be.equals(false);

      chai.expect(txns[1].id).to.be.equals("0x100000001"); // 2nd after beginMultiTxnOperation()
      chai.expect(txns[1].props.description).to.be.equals("insert element 0x40000000002");
      chai.expect(txns[1].sessionId).to.be.equals(1);
      chai.expect(txns[1].grouped).to.be.equals(true);
      chai.expect(txns[1].reversed).to.be.equals(false);

      chai.expect(txns[2].id).to.be.equals("0x100000002"); // 3rd after beginMultiTxnOperation() & before endMultiTxnOperation()
      chai.expect(txns[2].props.description).to.be.equals("insert element 0x40000000003");
      chai.expect(txns[2].sessionId).to.be.equals(1);
      chai.expect(txns[2].grouped).to.be.equals(true);
      chai.expect(txns[2].reversed).to.be.equals(false);

      chai.expect(txns[3].id).to.be.equals("0x100000003"); // 4th after endMultiTxnOperation()
      chai.expect(txns[3].props.description).to.be.equals("insert element 0x40000000004");
      chai.expect(txns[3].type).to.be.equals("Data");
      chai.expect(txns[3].grouped).to.be.equals(false);
      chai.expect(txns[3].reversed).to.be.equals(true);

      // reinstate the transaction
      chai.expect(b2.txns.isRedoPossible).to.be.equals(true);
      chai.expect(b2.txns.reinstateTxn()).to.be.equals(DbResult.BE_SQLITE_OK);

      txns = Array.from(b2.txns.queryTxns());

      chai.expect(txns[0].id).to.be.equals("0x100000000"); // 1st after beginMultiTxnOperation()
      chai.expect(txns[0].props.description).to.be.equals("insert element 0x40000000001");
      chai.expect(txns[0].sessionId).to.be.equals(1);
      chai.expect(txns[0].grouped).to.be.equals(false);
      chai.expect(txns[0].reversed).to.be.equals(false);

      chai.expect(txns[1].id).to.be.equals("0x100000001"); // 2nd after beginMultiTxnOperation()
      chai.expect(txns[1].props.description).to.be.equals("insert element 0x40000000002");
      chai.expect(txns[1].sessionId).to.be.equals(1);
      chai.expect(txns[1].grouped).to.be.equals(true);
      chai.expect(txns[1].reversed).to.be.equals(false);

      chai.expect(txns[2].id).to.be.equals("0x100000002"); // 3rd after beginMultiTxnOperation() & before endMultiTxnOperation()
      chai.expect(txns[2].props.description).to.be.equals("insert element 0x40000000003");
      chai.expect(txns[2].sessionId).to.be.equals(1);
      chai.expect(txns[2].grouped).to.be.equals(true);
      chai.expect(txns[2].reversed).to.be.equals(false);

      chai.expect(txns[3].id).to.be.equals("0x100000003"); // 4th after endMultiTxnOperation()
      chai.expect(txns[3].props.description).to.be.equals("insert element 0x40000000004");
      chai.expect(txns[3].type).to.be.equals("Data");
      chai.expect(txns[3].grouped).to.be.equals(false);
      chai.expect(txns[3].reversed).to.be.equals(false);

      chai.expect(b2.txns.isRedoPossible).to.be.equals(false);


      await testIModel.updateElement(b1Txn, e1);
      await testIModel.updateElement(b1Txn, e2, true);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: "update element 1 direct and 1 indirect" });

      const recomputeTxnIds = [] as TxnIdString[];
      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txn: TxnProps) => {
          return true;
        },
        recompute: async (txn: TxnProps): Promise<void> => {
          recomputeTxnIds.push(txn.id);
        },
      });

      await b2.pullChanges();

      chai.expect(recomputeTxnIds).to.deep.equals([
        "0x100000000",
        "0x100000001",
        "0x100000002",
        "0x100000003",
      ]);
    });
    it("abort rebase should discard in-memory changes", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "abort rebase discard memory b1");
      const b2Txn = startTestTxn(b2, "abort rebase discard memory b2");

      const e1 = await testIModel.insertElement(b1Txn);
      b1Txn.saveChanges();
      await b1.pushChanges({ description: `${e1} inserted` });

      const e2 = await testIModel.insertElement(b2Txn);
      chai.expect(e2).to.exist;
      let e3 = "";
      b2Txn.saveChanges();
      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txnProps: TxnProps) => {
          return true;
        },
        recompute: async (_txnProps: TxnProps) => {
          chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.true;
          e3 = await testIModel.insertElement(b2Txn);
          throw new Error("Rebase failed");
        },
      });

      chai.expect(b2.elements.tryGetElementProps(e1)).to.undefined;
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined;
      chai.expect(b2.changeset.index).to.equals(2);
      await chai.expect(b2.pullChanges()).to.be.rejectedWith("Rebase failed");

      chai.expect(b2.changeset.index).to.equals(3);
      chai.expect(e3).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e1)).to.exist;     // came from incoming changeset
      chai.expect(b2.elements.tryGetElementProps(e2)).to.undefined; // was local change and reversed during rebase.
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined; // was insert by reCompute() but due to exception the rebase attempt was abandoned.

      chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.true;

      // make temp change
      b2Txn.saveFileProperty({ name: "test", namespace: "testNamespace" }, "testValue");
      chai.expect(b2.txns.hasUnsavedChanges).is.true;

      chai.expect(b2.txns.rebaser.canAbort()).is.true;
      // should abort with unsaved local changes
      await b2.txns.rebaser.abort();

      chai.expect(b2.changeset.index).to.equals(2);
      chai.expect(b2.elements.tryGetElementProps(e1)).to.undefined; // reset briefcase should move tip back to where it was before pull
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;  // abort should put back e2 which was only change at the time of pull
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined; // add by rebase so should not exist either

      chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.false;
    });
    it("two users insert same ElementGroupsMembers instance", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "same ElementGroupsMembers b1");
      const b2Txn = startTestTxn(b2, "same ElementGroupsMembers b2");

      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn);
      const e3 = await testIModel.insertElement(b1Txn);
      const e4 = await testIModel.insertElement(b1Txn);

      chai.expect(e1).to.exist;
      chai.expect(e2).to.exist;
      chai.expect(e3).to.exist;
      chai.expect(e4).to.exist;

      b1Txn.saveChanges();
      await b1.pushChanges({ description: `inserted elements` });
      await b2.pullChanges();

      const r1 = b1Txn.insertRelationship(ElementGroupsMembers.create(b1, e1, e2, 10).toJSON());
      const r2 = b1Txn.insertRelationship(ElementGroupsMembers.create(b1, e3, e4, 20).toJSON());
      chai.expect(r1).to.exist;
      chai.expect(r2).to.exist;
      b1Txn.saveChanges();
      await b1.pushChanges({ description: `inserted relationship` });

      const r3 = b2Txn.insertRelationship(ElementGroupsMembers.create(b2, e1, e2, 10).toJSON());
      const r4 = b2Txn.insertRelationship(ElementGroupsMembers.create(b2, e3, e4, 20).toJSON());
      chai.expect(r3).to.exist;
      chai.expect(r4).to.exist;
      b2Txn.saveChanges();
      await b2.pushChanges({ description: `inserted relationship` });
      await b2.pullChanges();

      chai.expect(b2.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r1)).to.exist;
      chai.expect(b2.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r2)).to.exist;
      chai.expect(b2.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r3)).to.be.undefined;
      chai.expect(b2.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r4)).to.be.undefined;

      chai.expect(b1.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r1)).to.exist;
      chai.expect(b1.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r2)).to.exist;
      chai.expect(b1.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r3)).to.be.undefined;
      chai.expect(b1.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r4)).to.be.undefined;
    });
    it("one user update and other delete the link table relationships", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "relationship update delete b1");
      const b2Txn = startTestTxn(b2, "relationship update delete b2");

      const e1 = await testIModel.insertElement(b1Txn);
      const e2 = await testIModel.insertElement(b1Txn);

      const r1 = b1Txn.insertRelationship(ElementGroupsMembers.create(b1, e1, e2, 10).toJSON());
      const r2 = b1Txn.insertRelationship(ElementGroupsMembers.create(b1, e1, e2, 20).toJSON());

      chai.expect(e1).to.exist;
      chai.expect(e2).to.exist;
      chai.expect(r1).to.exist;
      chai.expect(r2).to.exist;

      b1Txn.saveChanges();
      await b1.pushChanges({ description: `inserted elements and relationship` });
      await b2.pullChanges();


      // intentionally change memberPriority to 10 for which there is another relationship already exists.
      chai.expect(() => b2Txn.updateRelationship({
        id: r1,
        classFullName: ElementGroupsMembers.classFullName,
        sourceId: e1,
        targetId: e2,
        memberPriority: 20
      } as RelationshipProps)).to.throws("error updating relationship");


      b2Txn.updateRelationship({
        id: r1,
        classFullName: ElementGroupsMembers.classFullName,
        sourceId: e1,
        targetId: e2,
        memberPriority: 60
      } as RelationshipProps);

      b1Txn.deleteRelationship({
        id: r1,
        classFullName: ElementGroupsMembers.classFullName,
        sourceId: e1,
        targetId: e2
      });

      b1Txn.saveChanges();
      await b1.pushChanges({ description: `deleted relationship` });

      b2Txn.saveChanges();
      await b2.pushChanges({ description: `updated relationship` });

      await b2.pullChanges();
      await b1.pullChanges();

      chai.expect(b1.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r1)).to.be.undefined;
      chai.expect(b1.relationships.tryGetInstanceProps(ElementGroupsMembers.classFullName, r2)).to.exist;
    });
    it("aborting rebaser in middle of rebase session where at least one txn is successfully rebased (used to cause crash)", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "abort rebaser mid session b1");
      const b2Txn = startTestTxn(b2, "abort rebaser mid session b2");

      const createTxn = async (txn: EditTxn) => {
        const id = await testIModel.insertElement(txn);
        chai.expect(id).is.exist;
        txn.saveChanges(`created element ${id}`);
        return id;
      };

      const e1 = await createTxn(b1Txn);
      await b1.pushChanges({ description: `${e1} inserted` });

      const e2 = await createTxn(b2Txn);
      const e3 = await createTxn(b2Txn);
      const e4 = await createTxn(b2Txn);

      let e5 = "";
      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txnProps: TxnProps) => {
          return true;
        },
        recompute: async (txnProps: TxnProps) => {
          chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.true;
          if (txnProps.id === "0x100000001") {
            e5 = await testIModel.insertElement(b2Txn);
            throw new Error("Rebase failed");
          }
        },
      });

      chai.expect(b2.elements.tryGetElementProps(e1)).to.be.undefined;
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e3)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e4)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e5)).to.be.undefined;
      chai.expect(b2.changeset.index).to.equals(2);
      await chai.expect(b2.pullChanges()).to.be.rejectedWith("Rebase failed");
      await chai.expect(createTxn(b2Txn)).to.be.rejectedWith(`Could not save changes (created element 0x40000000004)`);

      chai.expect(b2.changeset.index).to.equals(3);
      chai.expect(e3).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e1)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e3)).to.undefined;
      chai.expect(b2.elements.tryGetElementProps(e4)).to.undefined;
      chai.expect(b2.elements.tryGetElementProps(e5)).to.exist;

      chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.true;

      // make temp change
      b2Txn.saveFileProperty({ name: "test", namespace: "testNamespace" }, "testValue");
      chai.expect(b2.txns.hasUnsavedChanges).is.true;

      chai.expect(b2.txns.rebaser.canAbort()).is.true;

      // should abort with unsaved local changes
      await b2.txns.rebaser.abort();

      chai.expect(b2.changeset.index).to.equals(2);
      chai.expect(b2.elements.tryGetElementProps(e1)).to.be.undefined;
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e3)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e4)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e5)).to.be.undefined;

      chai.expect(BriefcaseManager.containsRestorePoint(b2, BriefcaseManager.PULL_MERGE_RESTORE_POINT_NAME)).is.false;

      b2.txns.rebaser.setCustomHandler({
        shouldReinstate: (_txnProps: TxnProps) => {
          return true;
        },
        recompute: async (_txnProps: TxnProps) => { },
      });

      const e6 = await createTxn(b2Txn);
      b2Txn.saveChanges(`created element ${e6}`);
      chai.expect(b2.txns.getCurrentTxnId()).to.equal("0x100000004");
      chai.expect(b2.txns.getLastSavedTxnProps()?.id).to.equal(`0x100000003`);

      await b2.pullChanges();
      chai.expect(b2.elements.tryGetElementProps(e1)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e3)).to.exist;
      chai.expect(b2.elements.tryGetElementProps(e4)).to.exist;
      const e7 = await createTxn(b2Txn);
      b2Txn.saveChanges(`created element ${e7}`);
      chai.expect(b2.txns.getCurrentTxnId()).to.equal("0x100000005");
      chai.expect(b2.txns.getLastSavedTxnProps()?.id).to.equal(`0x100000004`);
      await b2.pushChanges({ description: "pushed after rebase aborted" });

      await b1.pullChanges();
      chai.expect(b1.elements.tryGetElementProps(e1)).to.exist;
      chai.expect(b1.elements.tryGetElementProps(e2)).to.exist;
      chai.expect(b1.elements.tryGetElementProps(e3)).to.exist;
      chai.expect(b1.elements.tryGetElementProps(e4)).to.exist;
      chai.expect(b1.elements.tryGetElementProps(e7)).to.exist;
    });
    it("changeset DDL error are ignored and ec_* tables are used to reconstruct the sqlite tables", async () => {
      const b1 = await testIModel.openBriefcase();
      const b2 = await testIModel.openBriefcase();
      const b1Txn = startTestTxn(b1, "changeset ddl reconstruction b1");
      const iModelId = testIModel.iModelId;
      const targetDir = path.join(KnownTestLocations.outputDir, iModelId, "changesets");
      let ver = 0;
      let props = 0;
      const tblGeom2d = "bis_GeometricElement2d";
      const geom2dBaseColumnList = [
        "ElementId",
        "ECClassId",
        "CategoryId",
        "Origin_X",
        "Origin_Y",
        "Rotation",
        "BBoxLow_X",
        "BBoxLow_Y",
        "BBoxHigh_X",
        "BBoxHigh_Y",
        "GeometryStream",
        "TypeDefinitionId",
        "TypeDefinitionRelECClassId",
        "js1",
        "js2",
      ];

      const generateSchema = (noOfNewPropsToAdd: number) => {
        props += noOfNewPropsToAdd;
        return `<?xml version="1.0" encoding="UTF-8"?>
        <ECSchema schemaName="TestDomain1" alias="ts1" version="01.00.${ver++}" xmlns="http://www.bentley.com/schemas/Bentley.ECXML.3.2">
            <ECSchemaReference name="BisCore" version="01.00.00" alias="bis"/>
            <ECEntityClass typeName="test">
                <BaseClass>bis:GraphicalElement2d</BaseClass>
                <ECProperty propertyName="prop1" typeName="string" />
                ${Array.from({ length: props - 1 }, (_, i) => `<ECProperty propertyName="prop${i + 2}" typeName="string" />`).join("\n                ")}
            </ECEntityClass>
        </ECSchema>`;
      };

      const getColumnNames = (b: BriefcaseDb, tableName: string) => {
        return b.withSqliteStatement(`PRAGMA table_info(${tableName})`, (stmt) => {
          const columnNames: string[] = [];
          while (stmt.step() === DbResult.BE_SQLITE_ROW) {
            columnNames.push(stmt.getValue(1).getString());
          }
          return columnNames;
        });
      };

      const withLatestChangeset = async (cb: (reader: SqliteChangesetReader) => Promise<void>) => {
        const csInfo = await HubMock.getLatestChangeset({ iModelId });
        const info = await HubMock.downloadChangeset({
          iModelId,
          changeset: { id: csInfo.id },
          targetDir,
        });

        const reader = SqliteChangesetReader.openFile({ db: b1, fileName: info.pathname });
        try {
          await cb(reader);
        } finally {
          reader.close();
        }
      };

      // Verify initial columns
      chai.expect(getColumnNames(b1, tblGeom2d)).deep.equals(geom2dBaseColumnList);
      chai.expect(getColumnNames(b2, tblGeom2d)).deep.equals(geom2dBaseColumnList);

      // Import schema that add 5 new properties that should add 3 new shared columns
      await importSchemaStrings(b1Txn, [generateSchema(5)]);
      await b1.pushChanges({ description: `imported schema version 1.0.${ver - 1}` });

      // Verify columns after schema import
      chai.expect(getColumnNames(b1, tblGeom2d)).deep.equals([...geom2dBaseColumnList, "js3", "js4", "js5"]);

      //verify changeset has schema changes
      await withLatestChangeset(async (reader) => {
        const schemaChanges = reader.getDdlChanges()?.split(";");
        chai.expect(schemaChanges).to.include("ALTER TABLE [bis_GeometricElement2d] ADD COLUMN [js3] BLOB");
        chai.expect(schemaChanges).to.include("ALTER TABLE [bis_GeometricElement2d] ADD COLUMN [js4] BLOB");
        chai.expect(schemaChanges).to.include("ALTER TABLE [bis_GeometricElement2d] ADD COLUMN [js5] BLOB");
      });

      await b2.pullChanges();
      chai.expect(getColumnNames(b2, "bis_GeometricElement2d")).deep.equals([...geom2dBaseColumnList, "js3", "js4", "js5"]);


      // Import schema that add 5 new properties that should add 3 new shared columns
      await importSchemaStrings(b1Txn, [generateSchema(1)]);
      await b1.pushChanges({ description: `imported schema version 1.0.${ver - 1}` });

      // Verify columns after schema import
      chai.expect(getColumnNames(b1, tblGeom2d)).deep.equals([...geom2dBaseColumnList, "js3", "js4", "js5", "js6"]);
      //verify changeset has schema changes
      await withLatestChangeset(async (reader) => {
        const schemaChanges = reader.getDdlChanges()?.split(";");
        chai.expect(schemaChanges).to.include("ALTER TABLE [bis_GeometricElement2d] ADD COLUMN [js6] BLOB");
      });

      // delete the table so DDL apply should fail
      b2[_nativeDb].executeSql(`DROP TABLE ${tblGeom2d}`)

      // this would fail before this PR but should succeed as DDL error are ignored and table reconstruction is attempted using ec_* tables
      await b2.pullChanges();

      // Verify columns after schema import
      chai.expect(getColumnNames(b2, tblGeom2d)).deep.equals([...geom2dBaseColumnList, "js3", "js4", "js5", "js6"]);

      b1.close();
      b2.close();
    });
  });
}
